import { BentoLogo } from './BentoLogo';
import workspaceRestore from '../assets/workspace_restore.png';
import chromeProfiles from '../assets/chrome_profiles.png';
import devPrototyping from '../assets/dev-prototyping.png';
import snapToZone from '../assets/snaptozone.png';

interface FeatureRowProps {
  title: string;
  description: string;
  image: string;
  imageAlt: string;
  reversed?: boolean;
}

function FeatureRow({ title, description, image, imageAlt, reversed = false }: FeatureRowProps) {
  const imageBlock = (
    <div className="w-full lg:w-1/2">
      <div className="rounded-2xl overflow-hidden shadow-2xl">
        <img src={image} alt={imageAlt} className="w-full h-auto" />
      </div>
    </div>
  );

  const textBlock = (
    <div className="w-full lg:w-1/2 space-y-4" style={{ textAlign: 'left' }}>
      <h3 className="text-2xl md:text-3xl font-medium text-gray-900" style={{ textAlign: 'left' }}>{title}</h3>
      <p className="text-base md:text-lg text-gray-600 leading-relaxed" style={{ textAlign: 'left' }}>{description}</p>
    </div>
  );

  return (
    <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 items-center">
      {reversed ? (
        <>
          {textBlock}
          {imageBlock}
        </>
      ) : (
        <>
          {imageBlock}
          {textBlock}
        </>
      )}
    </div>
  );
}

export function Benefits() {
  const features = [
    {
      title: "Save & restore your setup perfectly.",
      description: "Your workspaces are saved exactly as you left them. Every app, every window position, every screen. Open them in a single click without the hassle of dragging apps around.",
      image: workspaceRestore,
      imageAlt: "Bento workspace launcher showing saved workspaces",
    },
    {
      title: "Save Chrome tabs with your workspace.",
      description: "Working on a project with specific documentation open? Save those URLs with your workspace. When you load it back, your browser opens right where you left off.",
      image: chromeProfiles,
      imageAlt: "Chrome browser tabs configuration in Bento",
    },
    {
      title: "Open apps to the right project.",
      description: "Save directory paths with your workspace. When you restore, your IDE and terminal open directly to the right project folder. No more navigating through folders to find your code.",
      image: devPrototyping,
      imageAlt: "Directory configuration for IDEs and terminals",
    },
    {
      title: "Snap windows to zones instantly.",
      description: "Inspired by Windows, drag any window to the top of your screen and snap it into predefined zones. Split, stack, columns, rows—organize your screen in seconds.",
      image: snapToZone,
      imageAlt: "Snap to zone window management feature",
    },
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-20">
          {/* Bento Logotype */}
          <div className="mb-8 md:mb-12">
            <BentoLogo className="h-16 md:h-20 lg:h-24 w-auto" />
          </div>

          {/* Subtitle */}
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-light mb-6 md:mb-8 max-w-[800px]">
            Ok Computer, let's work.
          </h2>

          {/* Description */}
          <p className="text-sm md:text-base leading-relaxed text-gray-600 max-w-[800px] mb-16 md:mb-20">
            We got tired of dragging windows around to organize our screens. Different tasks required different apps and setups. So we built Bento to solve an itch we've had for a while.
            <br />
            <br />
            Essentially, organize your workspace. Save it. Open the workspace when you return to that task.
            <br />
            <br />
            Simple as that.
          </p>

          {/* Video Player */}
          <div className="relative aspect-video rounded-3xl overflow-hidden shadow-2xl bg-black">
            <iframe
              src="https://www.youtube.com/embed/ElkB23UVALE?vq=hd1080&hd=1"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute top-0 left-0 w-full h-full"
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-neutral-50 py-16 md:py-24">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-20">
          {/* Features Section Header */}
          <div className="mb-12 md:mb-16">
            <p className="text-sm md:text-base text-gray-500 uppercase tracking-wider mb-4">Features</p>
            <h3 className="text-3xl md:text-4xl lg:text-5xl font-light mb-4">
              Why Bento?
            </h3>
            <p className="text-base md:text-lg text-gray-600 max-w-[600px]">
              Everything you need to organize your workspace.
            </p>
          </div>

          {/* Feature Rows */}
          <div className="space-y-20 md:space-y-32">
            {features.map((feature, index) => (
              <FeatureRow
                key={index}
                title={feature.title}
                description={feature.description}
                image={feature.image}
                imageAlt={feature.imageAlt}
                reversed={index % 2 === 1}
              />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
